/**
 * Wire-level filter operator strings used by the Vue ERP-compatible
 * `compositeFilter` / `simpleFilter` search contract (orders search,
 * metadata domain search, aggregation endpoints).
 *
 * Each key is a readable symbolic name; each value is the EXACT string
 * the backend expects on the wire. Centralising these prevents typos at
 * call sites and makes a future contract change a one-file edit.
 *
 * NOTE: This dialect (lowercase / symbolic) is the one used by the
 * `/v1/orders/search`, `/v1/users/metadata/domain/search`, and order
 * aggregation endpoints. The AR Contacts service uses a DIFFERENT
 * dialect (UPPER_CASE — e.g. `CONTAINS`, `EQUALS`, `STARTS_WITH`) and
 * intentionally does NOT consume this constant — see
 * `src/features/arContacts/types/payload.ts`.
 */
export const OPERATORS = {
  AMONG: 'among',
  EQUALS: '==',
  CONTAINS: 'contains',
  STARTS_WITH: 'startsWith',
  ENDS_WITH: 'endsWith',
  EXISTS: 'exists',
  JOIN: 'join',
  GREATER_THAN_OR_EQUALS_TO: '>=',
  GREATER_THAN: '>',
  LESS_THAN_OR_EQUALS_TO: '<=',
  LESS_THAN: '<',
  AMONG_TEXT: 'amongText',
} as const;

/** Union of every wire operator string (`'among' | '==' | ...`). */
export type FilterOperator = (typeof OPERATORS)[keyof typeof OPERATORS];

/**
 * Filter-node `type` discriminant — mirrors Vue's `FILTERS` constant
 * (`RW-ERP-Service/src/types/constants/api-constants.ts`).
 */
export const FILTERS = {
  SIMPLE_FILTER: 'simpleFilter',
  COMPOSITE_FILTER: 'compositeFilter',
} as const;

/**
 * `logicalOperator` values for a `compositeFilter` node — mirrors Vue's
 * `LOGICAL_OPERATOR` constant (same `api-constants.ts` source file).
 */
export const LOGICAL_OPERATOR = {
  AND: 'AND',
  OR: 'OR',
} as const;
